import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ReactNode } from 'react'
import { RxDragHandleDots2, RxTrash } from 'react-icons/rx'
import { cn } from '~/@/lib/utils'

interface SortableListItemProps {
  id: string
  onDelete: () => void
  children: ReactNode
  // Optional: allow parent to add additional refs (for floating UI, etc.)
  additionalRef?: (element: HTMLDivElement | null) => void
  disabled?: boolean
}

/**
 * Row chrome for a sortable list: drag handle, content, and a delete button shown on hover. Pairs with
 * `SortableList`, which owns the surrounding dnd context.
 */
export const SortableListItem = ({
  id,
  onDelete,
  children,
  additionalRef,
  disabled,
}: SortableListItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={(el) => {
        setNodeRef(el)
        additionalRef?.(el)
      }}
      className={cn(
        'doss-sortable-list-item hover:bg-gray-2 group relative flex items-center rounded px-1',
        isDragging && 'cursor-grabbing',
      )}
      style={style}
    >
      {!disabled && (
        <div {...attributes} {...listeners} className="shrink-0 cursor-grab active:cursor-grabbing">
          <RxDragHandleDots2 className="text-gray-8" size={16} />
        </div>
      )}

      <div className="min-w-0 flex-1 px-2">{children}</div>

      {/* Always laid out and hidden until hover, so revealing it can't reflow the row */}
      {!disabled && (
        <button
          className="invisible me-1 shrink-0 rounded p-1 text-red-9 transition-colors hover:bg-gray-4 group-hover:visible"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          title="Delete"
          type="button"
        >
          <RxTrash size={16} />
        </button>
      )}
    </div>
  )
}
